import { ILibraryMainStore } from '../../../pages/mylibrary/typings/stores';
import { caption, captionStrong } from '../../styles/commonStyles';
import { createStyles, makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { Colors, Icons } from 'copyright-clearance-center-common-ui';
import { RupPagination, RupSelect } from 'copyright-clearance-center-common-ui-react';
import { inject, observer } from 'mobx-react';
import React from 'react';

const useStyles = makeStyles(() =>
	createStyles({
		root: {
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			padding: '0.125rem 1rem',
			columnGap: '1rem',
			height: '100%'
		},
		itemsCountContainer: {
			display: 'flex',
			columnGap: '0.5rem'
		},
		itemsCount: {
			...captionStrong,
			cursor: 'default'
		},
		selectedItemsCount: {
			...captionStrong,
			cursor: 'default',
			color: Colors.primary500
		},
		pageSize: {
			display: 'flex',
			marginLeft: 'auto',
			columnGap: '0.25rem',
			alignItems: 'center',
			...caption
		},
		select: {
			'& .rup-select': {
				'& select': {
					border: 'none',
					padding: '0 1.15rem 0 .25rem',
					minHeight: '1rem',
					height: '1.5rem',
					textAlign: 'center'
				},
				lineHeight: '1.25rem'
			},
			'& .rup-select__icon': {
				right: '0.1rem'
			}
		},
		adjustButton: {
			marginTop: '0.125rem'
		}
	})
);

export interface IGridPaginationState {
	pageIndex: number;
	pageSize: number;
}

export interface IGridPaginationProps {
	count: number;
	selectedCount?: number;
	pageSizes?: number[];
	pagination: IGridPaginationState;
	onPaginationChanged: (pagination: IGridPaginationState) => void;
	loading?: boolean;
	testId?: string;
	refreshResults?: () => void;
}

const GridPagination: React.FunctionComponent<IGridPaginationProps> = props => {
	const initialRenderRef = React.useRef(true);
	const defaultPageSizes = [10, 20, 50, 100];

	const [pageSizes] = React.useState(props.pageSizes ?? defaultPageSizes);

	const onPageSizeChanged = (size: number) => {
		const newPagination = { pageIndex: 0, pageSize: size };
		onPaginationChanged(newPagination);
	};

	const onPageIndexChanged = (index: number) => {
		const newPagination = { pageIndex: index, pageSize: props.pagination.pageSize };
		onPaginationChanged(newPagination);
	};

	const onPaginationChanged = (pagination: IGridPaginationState) => {
		if (props.onPaginationChanged) {
			props.onPaginationChanged(pagination);
		}
	};

	let start = props.pagination.pageIndex * props.pagination.pageSize + 1;
	start = start > props.count ? props.count : start;
	let end = props.pagination.pageIndex * props.pagination.pageSize + props.pagination.pageSize;
	end = end > props.count ? props.count : end;
	const classes = useStyles();
	const pagesCount = Math.ceil(props.count / props.pagination.pageSize);
	return (
		<div className={classes.root} data-test-id={`${props.testId}-pagination`}>
			<div className={classes.itemsCountContainer}>
				{props.selectedCount && props.selectedCount > 0 ? (
					<div className={classes.selectedItemsCount}>
						{localize('{0} select of {1}', props.selectedCount, props.count)}
					</div>
				) : (
					<div className={classes.itemsCount}>
						{localize('{0}-{1} Items of {2}', start, end, props.count)}
					</div>
				)}
				<button
					type="button"
					className={clsx(classes.adjustButton, 'rup-icon-btn rup-icon-btn--xs rup-icon-btn--neutral')}
					data-test-id="SL-refresh-results"
					dangerouslySetInnerHTML={{ __html: Icons.RefreshIcon }}
					onClick={props.refreshResults}
				/>
			</div>
			<div className={classes.pageSize}>
				<div style={{ cursor: 'default' }}>{localize('Rows per page')}</div>
				<div className={classes.select}>
					<RupSelect
						placeholder=""
						disabled={props.loading}
						onSelectedItemChanged={item => {
							if (initialRenderRef.current) {
								initialRenderRef.current = false;
								return;
							}

							onPageSizeChanged(parseInt(item?.value ?? props.pagination.pageSize.toString()));
						}}
						testId={props.testId ? `${props.testId}-pagination-pageSize` : ''}
						value={props.pagination.pageSize.toString()}
						items={pageSizes.map(value => ({ value: value.toString(), text: value.toString() }))}
					/>
				</div>
			</div>
			<div>
				<RupPagination
					index={props.pagination.pageIndex}
					count={pagesCount}
					onPageIndexChanged={onPageIndexChanged}
					loading={props.loading}
					testId={props.testId}
				/>
			</div>
		</div>
	);
};

export default inject((stores: ILibraryMainStore) => ({
	loading: stores.libraryResultsStore.loadingInProgress,
	refreshResults: stores.libraryResultsStore.refresh
}))(observer(GridPagination));
